import React, { useState, useCallback } from "react";
import { Play, Pause, Download } from "lucide-react";
import { audioBufferToWav } from "../../utils/audioBufferToWav";

/**
 * Mix toolbar — appears between the beat audio player and takes section
 * when both are open. Provides "Play Both" and "Export Mix" controls.
 * Uses each player's own volume slider for mixing (no duplicate sliders).
 */
const MixToolbar = ({
  beatWaveSurfer,
  takeWaveSurfer,
  getTakeAudioUrl,
  getBeatAudioUrl,
  isMobile = false,
  autoPlay = false,
  onAutoPlayChange = null,
  shiftOffsetSec = 0,
}) => {
  const [isBothPlaying, setIsBothPlaying] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);

  // Play both beat and take simultaneously (with optional shift offset)
  const togglePlayBoth = useCallback(() => {
    if (!beatWaveSurfer || !takeWaveSurfer) return;

    if (isBothPlaying) {
      beatWaveSurfer.pause();
      takeWaveSurfer.pause();
      setIsBothPlaying(false);
    } else {
      // Seek both to start for sync
      beatWaveSurfer.seekTo(0);
      takeWaveSurfer.seekTo(0);
      setIsBothPlaying(true);

      if (shiftOffsetSec > 0) {
        // Positive shift: beat starts immediately, take starts after delay
        beatWaveSurfer.play();
        setTimeout(() => takeWaveSurfer.play(), shiftOffsetSec * 1000);
      } else if (shiftOffsetSec < 0) {
        // Negative shift: take starts immediately, beat starts after delay
        takeWaveSurfer.play();
        setTimeout(
          () => beatWaveSurfer.play(),
          Math.abs(shiftOffsetSec) * 1000,
        );
      } else {
        // No shift: both start immediately
        beatWaveSurfer.play();
        takeWaveSurfer.play();
      }
    }
  }, [beatWaveSurfer, takeWaveSurfer, isBothPlaying, shiftOffsetSec]);

  // Stop when either finishes
  const handleFinish = useCallback(() => {
    if (beatWaveSurfer) beatWaveSurfer.pause();
    if (takeWaveSurfer) takeWaveSurfer.pause();
    setIsBothPlaying(false);
  }, [beatWaveSurfer, takeWaveSurfer]);

  // Attach finish listeners
  React.useEffect(() => {
    if (!beatWaveSurfer || !takeWaveSurfer) return;

    beatWaveSurfer.on("finish", handleFinish);
    takeWaveSurfer.on("finish", handleFinish);

    return () => {
      beatWaveSurfer.un("finish", handleFinish);
      takeWaveSurfer.un("finish", handleFinish);
    };
  }, [beatWaveSurfer, takeWaveSurfer, handleFinish]);

  /**
   * Export Mix — merge beat + take into a single MP3 using Web Audio API.
   * Uses OfflineAudioContext to render the mix, then encodes with lamejs.
   */
  const handleExportMix = useCallback(async () => {
    if (isExporting) return;

    const beatUrl = getBeatAudioUrl && (await getBeatAudioUrl());
    const takeUrl = getTakeAudioUrl && (await getTakeAudioUrl());

    if (!beatUrl || !takeUrl) {
      console.error("❌ Cannot export mix: missing audio URLs");
      return;
    }

    setIsExporting(true);
    setExportProgress(0);

    try {
      // Fetch both audio files as ArrayBuffers
      setExportProgress(10);
      const [beatResponse, takeResponse] = await Promise.all([
        fetch(beatUrl),
        fetch(takeUrl),
      ]);

      setExportProgress(25);
      const [beatBuffer, takeBuffer] = await Promise.all([
        beatResponse.arrayBuffer(),
        takeResponse.arrayBuffer(),
      ]);

      // Decode audio data
      setExportProgress(35);
      const audioContext = new (
        window.AudioContext || window.webkitAudioContext
      )();
      const [beatAudio, takeAudio] = await Promise.all([
        audioContext.decodeAudioData(beatBuffer),
        audioContext.decodeAudioData(takeBuffer),
      ]);
      audioContext.close();

      // Calculate mix duration accounting for shift offset
      const absShift = Math.abs(shiftOffsetSec);
      const mixDuration =
        Math.max(beatAudio.duration, takeAudio.duration) + absShift;
      const sampleRate = beatAudio.sampleRate;
      const channels = Math.max(
        beatAudio.numberOfChannels,
        takeAudio.numberOfChannels,
      );

      setExportProgress(50);

      // Create offline context for rendering
      const offlineCtx = new OfflineAudioContext(
        channels,
        Math.ceil(mixDuration * sampleRate),
        sampleRate,
      );

      // Create sources with volume from each player's current level
      const beatSource = offlineCtx.createBufferSource();
      beatSource.buffer = beatAudio;
      const beatGain = offlineCtx.createGain();
      beatGain.gain.value = beatWaveSurfer ? beatWaveSurfer.getVolume() : 1;
      beatSource.connect(beatGain);
      beatGain.connect(offlineCtx.destination);

      const takeSource = offlineCtx.createBufferSource();
      takeSource.buffer = takeAudio;
      const takeGain = offlineCtx.createGain();
      takeGain.gain.value = takeWaveSurfer ? takeWaveSurfer.getVolume() : 1;
      takeSource.connect(takeGain);
      takeGain.connect(offlineCtx.destination);

      // Apply shift offset: positive = take delayed, negative = beat delayed
      if (shiftOffsetSec > 0) {
        beatSource.start(0);
        takeSource.start(shiftOffsetSec);
      } else if (shiftOffsetSec < 0) {
        beatSource.start(absShift);
        takeSource.start(0);
      } else {
        beatSource.start(0);
        takeSource.start(0);
      }

      setExportProgress(65);

      // Render the mix
      const renderedBuffer = await offlineCtx.startRendering();

      setExportProgress(80);

      // Encode as WAV (lamejs for MP3 can be added later)
      const wavBlob = audioBufferToWav(renderedBuffer);

      setExportProgress(95);

      // Download
      const downloadUrl = URL.createObjectURL(wavBlob);
      try {
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `mix-${Date.now()}.wav`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } finally {
        URL.revokeObjectURL(downloadUrl);
      }

      setExportProgress(100);
      console.log("✅ Mix exported successfully");
    } catch (err) {
      console.error("❌ Export mix error:", err);
    } finally {
      setIsExporting(false);
      setExportProgress(0);
    }
  }, [
    isExporting,
    getBeatAudioUrl,
    getTakeAudioUrl,
    beatWaveSurfer,
    takeWaveSurfer,
    shiftOffsetSec,
  ]);

  const bothReady = beatWaveSurfer && takeWaveSurfer;

  return (
    <div
      className={`flex items-center gap-2 px-2 py-1.5 bg-forest-900/60 border-y border-forest-600/50 ${
        isMobile ? "justify-center" : ""
      }`}
      style={{ minHeight: "36px" }}
    >
      {/* Play Both */}
      <button
        onClick={togglePlayBoth}
        disabled={!bothReady}
        className={`flex items-center gap-1.5 px-3 py-1 text-xs rounded transition-colors ${
          !bothReady
            ? "opacity-40 cursor-not-allowed bg-forest-700 text-forest-400"
            : isBothPlaying
              ? "bg-forest-600 text-gold-500 border border-gold-500/30"
              : "bg-forest-700 hover:bg-forest-600 text-forest-200"
        }`}
        title="Play beat and take together"
      >
        {isBothPlaying ? (
          <Pause className="w-3.5 h-3.5" />
        ) : (
          <Play className="w-3.5 h-3.5" />
        )}
        <span>{isBothPlaying ? "Stop" : "Play Both"}</span>
      </button>

      {/* Auto-play beat when recording starts */}
      {onAutoPlayChange && (
        <button
          onClick={() => onAutoPlayChange(!autoPlay)}
          className={`flex items-center gap-1.5 px-3 py-1 text-xs rounded transition-colors ${
            autoPlay
              ? "bg-forest-600 text-gold-500 border border-gold-500/30"
              : "bg-forest-700 hover:bg-forest-600 text-forest-200"
          }`}
          title="Auto-play beat when recording starts"
        >
          <span>Auto</span>
        </button>
      )}

      {/* Export Mix */}
      <button
        onClick={handleExportMix}
        disabled={!bothReady || isExporting}
        className={`flex items-center gap-1.5 px-3 py-1 text-xs rounded transition-colors ${
          !bothReady || isExporting
            ? "opacity-40 cursor-not-allowed bg-forest-700 text-forest-400"
            : "bg-forest-700 hover:bg-forest-600 text-forest-200"
        }`}
        title="Export beat + take as one file"
      >
        <Download className="w-3.5 h-3.5" />
        <span>
          {isExporting ? `Exporting ${exportProgress}%` : "Export Mix"}
        </span>
      </button>
    </div>
  );
};

export default MixToolbar;
